import { MagnifyingGlassIcon } from '@rsos/assets/icons';
import CalendarPicker from '@rsos/base-components/CalendarPicker';
import { SEVEN_YEARS } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { SESSION_TYPES } from '../../../constants';
import styles from './ConversationHistory.module.scss';

interface ToolbarProps {
  availableSessionTypes: typeof SESSION_TYPES;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  startDate: Date;
  endDate: Date;
  now: number;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  selectedTypes: string[];
  onToggleType: (value: string) => void;
  checkedIds: string[];
  onDownload: (ids: string[]) => void;
  onClearSelection: () => void;
  isDownloading: boolean;
  isLoading: boolean;
}

const Toolbar = ({
  availableSessionTypes,
  searchTerm,
  onSearchChange,
  startDate,
  endDate,
  now,
  onStartDateChange,
  onEndDateChange,
  selectedTypes,
  onToggleType,
  checkedIds,
  onDownload,
  onClearSelection,
  isDownloading,
  isLoading,
}: ToolbarProps) => {
  const selectedCount = checkedIds.length;
  const hasSelection = selectedCount > 0;
  // Disable the filter controls while a fetch is in flight so rapid clicks
  // don't queue overlapping refetches and flash the table. The search input is
  // left enabled — it's debounced, so it can't spam, and disabling it would
  // interrupt typing mid-query.
  const controlsDisabled = isLoading || isDownloading;
  return (
    <div className={styles.toolbar}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Search</span>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon}>
            <MagnifyingGlassIcon width={14} height={14} />
          </span>
          <input
            className={styles.searchInput}
            data-name="conversation-search-input"
            type="text"
            placeholder="Phone number..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.filterDivider} />

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Date Range</span>
        <div className={styles.dateRange}>
          <CalendarPicker
            dataName="conversation-start-date"
            selectedDate={startDate}
            onChange={date => date && onStartDateChange(date)}
            popperPlacement="bottom-start"
            minDate={new Date(now - SEVEN_YEARS)}
            maxDate={endDate}
            disabled={controlsDisabled}
          />
          <span className={styles.dateSeparator}>–</span>
          <CalendarPicker
            dataName="conversation-end-date"
            selectedDate={endDate}
            onChange={date => date && onEndDateChange(date)}
            popperPlacement="bottom-start"
            minDate={startDate}
            maxDate={new Date(now)}
            disabled={controlsDisabled}
          />
        </div>
      </div>

      <div className={styles.filterDivider} />

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Type</span>
        <div className={styles.typeFilter}>
          {availableSessionTypes.map(({ value, label }) => (
            <button
              key={value}
              className={`${styles.typeChip}${selectedTypes.includes(value) ? ` ${styles.typeChipActive}` : ''}`}
              data-name="conversation-type-chip"
              aria-pressed={selectedTypes.includes(value)}
              onClick={() => onToggleType(value)}
              disabled={controlsDisabled}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.downloadGroup}>
        {hasSelection && (
          <>
            <button
              className={styles.downloadBtn}
              data-name="conversation-bulk-download"
              onClick={() => onDownload(checkedIds)}
              disabled={controlsDisabled}
            >
              {`Download (${selectedCount})`}
            </button>
            <button
              className={styles.clearBtn}
              data-name="conversation-clear-selection"
              onClick={onClearSelection}
              disabled={controlsDisabled}
            >
              Clear
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
